import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Alert, Divider, Form, Input, Select, Space, Typography } from "antd";
import { useState } from "react";
import {
	useCreateAgent,
	useDeploymentOptions,
} from "@/common/artemis-operations/agents";
import { useEnvironments } from "@/common/artemis-operations/environments";
import { AWS_REGIONS } from "@/common/aws";
import {
	AGENT_CREATED_DESC,
	AGENT_CREATED_TITLE,
	ERROR_AGENT_CREATION_DESC,
	ERROR_AGENT_CREATION_TITLE,
} from "@/common/messages";
import type { AgentCredentialReveal } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { environmentCanDeploy } from "@/components/environments/environment-base";
import { Wizard } from "@/components/wizard";
import { SegmentedSelection } from "../segmented-selection";
import { AgentCredentialModal } from "./agent-credential-modal";
import { PROVIDER_DEPLOY_FIELDS } from "./agent-deploy-fields";

interface AgentCreateWizardProps {
	open: boolean;
	onClose: () => void;
}

enum AgentMode {
	// ARTEMIS ("StratoNext Cloud" / platform-hosted) is postponed — removed from the wizard while the
	// backend keeps the hosted path behind a flag. Re-add here to restore the option.
	// EXTERNAL will later grow optional A2A connectivity (endpoint URL, auth) so the platform can
	// also push to a reachable external agent; the machine credential stays mandatory either way.
	REMOTE = "REMOTE",
	EXTERNAL = "EXTERNAL",
}

/** Secret-expiry choices for an External agent's credential; days, or null for never. */
const EXPIRY_OPTIONS: { value: number | 0; label: string }[] = [
	{ value: 90, label: "90 days" },
	{ value: 365, label: "1 year" },
	{ value: 0, label: "Never" },
];

/** Wizard for creating (registering) a new agent. */
export function AgentCreateWizard({ open, onClose }: AgentCreateWizardProps) {
	const { workspaceId } = useParams({ strict: false }) as {
		workspaceId: string;
	};

	const [loading, setLoading] = useState(false);
	// The one-time credential reveal for a just-created External agent (ADR-050). Held here so the
	// modal survives the wizard closing; dropped for good once the user acknowledges it.
	const [credentials, setCredentials] = useState<AgentCredentialReveal | null>(
		null,
	);
	const [form] = Form.useForm();
	const agentMode: AgentMode = Form.useWatch("mode", form) ?? AgentMode.REMOTE;
	const environmentId: string | undefined = Form.useWatch(
		"environmentId",
		form,
	);
	const createAgent = useCreateAgent();
	const { data: environments } = useEnvironments(workspaceId);
	const deployableEnvironments = environments?.filter(
		(e) => e && environmentCanDeploy(e),
	);
	const { data: deploymentOptions } = useDeploymentOptions();
	const awsRegions = deploymentOptions?.awsRegions ?? [];
	const regionOptions = awsRegions.map((r) => ({
		value: r,
		label: AWS_REGIONS.find((x) => x.value === r)?.label ?? r,
	}));
	// Provider-specific deploy-time settings for the selected remote environment (AWS -> region).
	const selectedEnv = deployableEnvironments?.find(
		(e) => e?.id === environmentId,
	);
	const deployFields = selectedEnv
		? (PROVIDER_DEPLOY_FIELDS[selectedEnv.provider] ?? [])
		: [];
	const notify = useNotify();
	const queryClient = useQueryClient();

	const reset = () => {
		form.resetFields();
		setLoading(false);
	};

	const handleComplete = () => {
		form.validateFields().then((values) => {
			setLoading(true);
			// Generic deploy settings from the provider's deploy fields (AWS -> Region).
			const deploymentSettings =
				agentMode === AgentMode.REMOTE && selectedEnv
					? (PROVIDER_DEPLOY_FIELDS[selectedEnv.provider] ?? [])
							.map((f) => ({
								key: f.key,
								value: (values[`deploy_${f.key}`] as string | undefined) ?? "",
							}))
							.filter((kv) => kv.value.length > 0)
					: undefined;
			const expiryDays: number | undefined =
				agentMode === AgentMode.EXTERNAL ? values.secretExpiry : undefined;
			createAgent(
				{
					name: values.name,
					workspaceId,
					kind: agentMode === AgentMode.EXTERNAL ? "EXTERNAL" : undefined,
					environmentId:
						agentMode === AgentMode.REMOTE ? values.environmentId : undefined,
					clientSecretExpiresAt: expiryDays
						? new Date(
								Date.now() + expiryDays * 24 * 60 * 60 * 1000,
							).toISOString()
						: undefined,
					deploymentSettings: deploymentSettings?.length
						? deploymentSettings
						: undefined,
				},
				{
					onSuccess: (payload) => {
						notify.success(AGENT_CREATED_TITLE, AGENT_CREATED_DESC);
						queryClient.invalidateQueries({ queryKey: ["agents"] });
						// External agents: surface the one-time secret before it is gone forever.
						setCredentials(payload.credentials ?? null);
						onClose();
						reset();
					},
					onError: () => {
						setLoading(false);
						notify.error(ERROR_AGENT_CREATION_TITLE, ERROR_AGENT_CREATION_DESC);
					},
				},
			);
		});
	};

	return (
		<>
			<AgentCredentialModal
				credentials={credentials}
				onClose={() => setCredentials(null)}
			/>
			<Wizard
				open={open}
				title="Create Agent"
				completeLabel="Create"
				onComplete={handleComplete}
				form={form}
				loading={loading}
				loadingMessage="Creating agent…"
				onCancel={() => {
					onClose();
					reset();
				}}
				pages={[
					{
						id: "mode",
						title: "Mode",
						content: (
							<>
								<Form.Item
									name="mode"
									noStyle
									initialValue={AgentMode.REMOTE}
									rules={[{ required: true }]}
								>
									<SegmentedSelection
										options={[
											{
												value: AgentMode.REMOTE,
												label: "Background Agent",
												description:
													"Deploy a StratoNext agent into one of your connected environments.",
											},
											{
												value: AgentMode.EXTERNAL,
												label: "External Agent",
												description:
													"Register an agent that runs outside the platform, on your machine or elsewhere.",
											},
										]}
									/>
								</Form.Item>
								<Alert
									type="info"
									style={{ marginTop: 12 }}
									message={
										agentMode === AgentMode.REMOTE
											? "The agent will be deployed to one of your connected environments. The agent is A2A enabled and will be available for background work."
											: "This creates an agent identity that an existing agent can use to authenticate with the StratoNext API. Automatic task dispatch requires A2A support."
									}
								/>
								{agentMode === AgentMode.REMOTE && (
									<Alert
										type="warning"
										style={{ marginTop: 12 }}
										message={
											"The agent uses the Amazon Bedrock API in your AWS account. Please enable model access once before using the agent. All model usage charges will be billed directly to your AWS account."
										}
									/>
								)}
							</>
						),
					},
					{
						id: "details",
						title: "Details",
						content: (
							<Space
								orientation="vertical"
								size={"small"}
								style={{ width: "100%" }}
							>
								<Form.Item
									name="name"
									label="Name"
									rules={[{ required: true, min: 3 }]}
								>
									<Input placeholder="Agent name" />
								</Form.Item>

								{agentMode === AgentMode.EXTERNAL && (
									<Space
										orientation="vertical"
										size={"small"}
										style={{ width: "100%" }}
									>
										<Divider titlePlacement={"center"}>
											Machine Credential
										</Divider>
										<Form.Item
											name="secretExpiry"
											label="Secret expiry"
											initialValue={90}
											extra="After expiry the credential stops minting tokens; rotate it to issue a new secret."
										>
											<Select options={EXPIRY_OPTIONS} />
										</Form.Item>
										<Typography.Text type="secondary">
											The client secret is shown once after creation. Store it
											on the machine that runs the agent. StratoNext keeps only
											a hash.
										</Typography.Text>
									</Space>
								)}

								{agentMode === AgentMode.REMOTE && (
									<Space
										orientation="vertical"
										size={"small"}
										style={{ width: "100%" }}
									>
										<Divider titlePlacement={"center"}>
											Remote Environment
										</Divider>
										<Form.Item
											name="environmentId"
											label="Environment"
											extra="Only environments that allow agent deployment are shown."
											rules={[{ required: agentMode === AgentMode.REMOTE }]}
										>
											<Select
												placeholder="Select an environment"
												options={deployableEnvironments?.map((e) => ({
													value: String(e?.id),
													label: e?.name,
												}))}
												onChange={(val) => {
													// Default the region to the env's DefaultRegion when supported.
													const env = deployableEnvironments?.find(
														(e) => e?.id === val,
													);
													const dr = env?.metadata?.find(
														(m) => m?.key === "DefaultRegion",
													)?.value;
													form.setFieldValue("deploy_Region", dr || undefined);
												}}
											/>
										</Form.Item>

										{deployFields.map((field) => (
											<Form.Item
												key={field.key}
												name={`deploy_${field.key}`}
												label={field.label}
												extra={field.description}
												rules={[{ required: field.required }]}
											>
												<Select
													placeholder="Select a region"
													options={regionOptions}
												/>
											</Form.Item>
										))}
									</Space>
								)}
							</Space>
						),
					},
				]}
			/>
		</>
	);
}
